import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIconButton from "components/common/DeleteOutlineIconButton";
import { FieldArray, Form, Formik, type FormikErrors } from "formik";
import { AddressAutocomplete } from "components/common/AddressAutocomplete";
import { InputBase, InputLabel as StandardInputLabel } from "components/common/Input";
import * as Yup from "yup";
import { api } from "services/api.service";
import useLocations from "hooks/locations/useLocations";
import type { Asset } from "types/asset";
import type { Employee } from "types/employee";
import type { Location } from "types/location";
import type {
  WorkOrder,
  WorkOrderTask,
  WorkOrderTaskFormRow,
  WorkOrderLocationSummary,
  WorkOrderFormSubmitPayload,
} from "types/workOrder";
import {
  employeeHasShipTo,
  formatEmployeeShipToDisplay,
  formatWorkOrderLocation,
} from "./workOrderDisplay";

const inputWhiteSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
  },
};

const ACTION_OPTIONS = [
  { value: "new_starter", label: "New Starter" },
  { value: "leaver", label: "Leaver" },
  { value: "new_device_required", label: "New Device Required" },
  { value: "repair", label: "Repair" },
  { value: "warranty_return", label: "Warranty Return" },
] as const;

const ASSET_ACTIONS = new Set(["repair", "warranty_return"]);

function newRowKey() {
  return `new-${crypto.randomUUID()}`;
}

function employeeAutocompleteLabel(o: Employee): string {
  const name = [o.firstName, o.lastName].filter(Boolean).join(" ") || o.email || "";
  const ext = o.externalEmployeeId?.trim();
  if (ext) return `${name} (${ext})`;
  return name;
}

function emptyTask(): WorkOrderTaskFormRow {
  return {
    key: newRowKey(),
    action: "new_device_required",
    employeeId: null,
    assetId: null,
    locationId: null,
    employee: null,
    asset: null,
    location: null,
    description: "",
  };
}

function taskFromApi(t: WorkOrderTask, index: number): WorkOrderTaskFormRow {
  const we = t.employee;
  const officeSrc = we?.officeLocation ?? we?.office_location;
  const officeLoc = officeSrc
    ? ({
        id: String(officeSrc.id),
        uuid: officeSrc.uuid,
        name: officeSrc.name ?? "",
        address: officeSrc.address ?? "",
        address2: officeSrc.address2 ?? "",
        city: officeSrc.city ?? "",
        state: officeSrc.state ?? "",
        zipcode: officeSrc.zipcode ?? "",
        country: officeSrc.country ?? "",
        description: "",
      } as Location)
    : null;

  const emp = we
    ? ({
        id: we.id,
        uuid: we.uuid,
        externalEmployeeId: we.externalEmployeeId ?? we.external_employee_id ?? null,
        firstName: we.firstName ?? we.first_name,
        lastName: we.lastName ?? we.last_name,
        email: we.email,
        status: "active",
        officeLocation: officeLoc,
        officeLocationId: officeSrc?.id ?? (officeLoc ? Number(officeLoc.id) : null),
        homeAddress: we.homeAddress ?? we.home_address ?? null,
        homeAddress2: we.homeAddress2 ?? we.home_address2 ?? null,
        homeCity: we.homeCity ?? we.home_city ?? null,
        homeState: we.homeState ?? we.home_state ?? null,
        homeZipcode: we.homeZipcode ?? we.home_zipcode ?? null,
        homeCountry: we.homeCountry ?? we.home_country ?? null,
      } as Employee)
    : null;

  const taskLoc = t.location
    ? ({
        id: t.location.id,
        uuid: t.location.uuid,
        name: t.location.name,
        address: t.location.address,
        city: t.location.city,
      } as WorkOrderLocationSummary)
    : null;

  return {
    key: t.uuid || `t-${index}`,
    id: t.id,
    uuid: t.uuid,
    action: t.action || "warranty_return",
    status: t.status,
    employeeId: t.employee?.id ?? null,
    assetId: t.asset?.id ?? null,
    locationId: t.location?.id ?? null,
    employee: emp,
    asset: t.asset ?? null,
    location: taskLoc,
    description: t.description ?? "",
  };
}

function initialTasksFromWorkOrder(wo: Partial<WorkOrder>): WorkOrderTaskFormRow[] {
  const tasks = wo.tasks;
  if (tasks && tasks.length > 0) {
    return tasks.map((t, i) => taskFromApi(t, i));
  }
  return [emptyTask()];
}

export type { WorkOrderFormSubmitPayload };

interface WorkOrderFormProps {
  initialValues: Partial<WorkOrder>;
  onSubmit: (values: WorkOrderFormSubmitPayload) => void | Promise<void>;
}

function buildTicketTasksAttributes(tasks: WorkOrderTaskFormRow[]) {
  const out: WorkOrderFormSubmitPayload["ticket_tasks_attributes"] = [];
  let pos = 0;
  for (const t of tasks) {
    if (t._destroy) {
      if (t.id) {
        out.push({ id: t.id, _destroy: "1" });
      }
      continue;
    }
    out.push({
      id: t.id,
      uuid: t.uuid,
      position: pos,
      action: t.action,
      employee_id: t.employeeId,
      asset_id: t.assetId,
      location_id: t.locationId,
      description: t.description?.trim() || null,
    });
    pos += 1;
  }
  return out;
}

const validationSchema = Yup.object({
  reason_for_return: Yup.string().nullable(),
});

function locationToSummary(loc: Location): WorkOrderLocationSummary {
  return {
    id: Number(loc.id),
    uuid: loc.uuid,
    name: loc.name,
    address: loc.address,
    city: loc.city,
  };
}

function TaskLine({
  task,
  index,
  isEdit,
  lineLabel,
  setFieldValue,
  errors,
  touched,
  canRemove,
  onRemove,
  locations,
}: {
  task: WorkOrderTaskFormRow;
  index: number;
  isEdit: boolean;
  lineLabel: number;
  setFieldValue: (field: string, value: unknown) => void;
  errors: FormikErrors<{ tasks: WorkOrderTaskFormRow[] }>;
  touched: { tasks?: boolean | Array<Record<string, boolean>> };
  canRemove: boolean;
  onRemove: () => void;
  locations: Location[];
}) {
  const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([]);
  const [assetOptions, setAssetOptions] = useState<Asset[]>([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  const [inlineOpen, setInlineOpen] = useState(false);
  const empTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (empTimer.current) clearTimeout(empTimer.current);
      if (assetTimer.current) clearTimeout(assetTimer.current);
    };
  }, []);

  const taskErrors =
    Array.isArray(errors.tasks) && errors.tasks[index]
      ? (errors.tasks[index] as FormikErrors<WorkOrderTaskFormRow>)
      : undefined;
  const taskTouched = Array.isArray(touched.tasks) ? (touched.tasks[index] as Record<string, boolean> | undefined) : undefined;

  const searchEmployees = useCallback(async (q: string) => {
    if (!q.trim()) {
      setEmployeeOptions([]);
      return;
    }
    setEmpLoading(true);
    try {
      const { data } = await api.get<{ employees: Employee[] }>(`/employees?search=${encodeURIComponent(q.trim())}`);
      setEmployeeOptions(data.employees || []);
    } catch {
      setEmployeeOptions([]);
    } finally {
      setEmpLoading(false);
    }
  }, []);

  const searchAssets = useCallback(async (q: string) => {
    if (!q.trim()) {
      setAssetOptions([]);
      return;
    }
    setAssetLoading(true);
    try {
      const { data } = await api.get<{ assets: Asset[] }>(
        `/assets?search=${encodeURIComponent(q.trim())}&autocomplete=true&page[number]=1&page[size]=50`
      );
      setAssetOptions(data.assets || []);
    } catch {
      setAssetOptions([]);
    } finally {
      setAssetLoading(false);
    }
  }, []);

  const shipToDisplay = () => {
    if (task.locationId && task.location) {
      return formatWorkOrderLocation(task.location);
    }
    return formatEmployeeShipToDisplay(task.employee);
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: "divider" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Request {lineLabel}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isEdit && task.status ? <Chip size="small" label={task.status} variant="outlined" /> : null}
            {canRemove ? (
              <DeleteOutlineIconButton aria-label="Remove line" onClick={onRemove} />
            ) : null}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth sx={inputWhiteSx} required>
              <InputLabel id={`action-${task.key}`}>Action</InputLabel>
              <Select
                labelId={`action-${task.key}`}
                label="Action"
                value={task.action}
                onChange={(e) => setFieldValue(`tasks.${index}.action`, e.target.value)}
              >
                {ACTION_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={8}>
            <Autocomplete
              options={employeeOptions}
              value={task.employee}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(o) => employeeAutocompleteLabel(o)}
              loading={empLoading}
              onChange={(_, v) => {
                const emp = v && typeof v === "object" && "id" in v ? (v as Employee) : null;
                setFieldValue(`tasks.${index}.employee`, emp);
                setFieldValue(`tasks.${index}.employeeId`, emp?.id ?? null);
                setInlineOpen(false);
              }}
              onInputChange={(_, input, reason) => {
                if (reason !== "input") return;
                if (empTimer.current) clearTimeout(empTimer.current);
                empTimer.current = setTimeout(() => void searchEmployees(input), 280);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee (type to search)"
                  error={Boolean(taskTouched?.employeeId && taskErrors?.employeeId)}
                  helperText={taskTouched?.employeeId && taskErrors?.employeeId}
                  sx={inputWhiteSx}
                />
              )}
            />
            {!inlineOpen ? (
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setInlineOpen(true)}
                sx={{ display: "block", mt: 1, textAlign: "left", cursor: "pointer" }}
              >
                Add new employee
              </Link>
            ) : null}
            {inlineOpen ? (
              <Box sx={{ mt: 2 }}>
                <InlineEmployeeForm
                  addressInputId={`wo-inline-addr-${task.key}`}
                  onDismiss={() => setInlineOpen(false)}
                  onCreated={(emp) => {
                    setFieldValue(`tasks.${index}.employee`, emp);
                    setFieldValue(`tasks.${index}.employeeId`, emp.id);
                    setInlineOpen(false);
                  }}
                />
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Ship to
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {shipToDisplay()}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={inputWhiteSx}>
              <InputLabel id={`loc-${task.key}`}>Delivery location (optional)</InputLabel>
              <Select
                labelId={`loc-${task.key}`}
                label="Delivery location (optional)"
                value={task.locationId != null ? String(task.locationId) : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "") {
                    setFieldValue(`tasks.${index}.locationId`, null);
                    setFieldValue(`tasks.${index}.location`, null);
                    return;
                  }
                  const id = Number(v);
                  const loc = locations.find((l) => Number(l.id) === id);
                  setFieldValue(`tasks.${index}.locationId`, id);
                  setFieldValue(`tasks.${index}.location`, loc ? locationToSummary(loc) : null);
                }}
              >
                <MenuItem value="">
                  <em>Use employee address</em>
                </MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.uuid || loc.id} value={String(loc.id)}>
                    {loc.name || loc.address || loc.city || `Location ${loc.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={assetOptions}
              value={task.asset as Asset | null}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(o) =>
                [o.assetTag, o.serialNumber].filter(Boolean).join(" · ") || o.model || ""
              }
              loading={assetLoading}
              onChange={(_, v) => {
                const asset = v && typeof v === "object" && "id" in v ? (v as Asset) : null;
                setFieldValue(`tasks.${index}.asset`, asset);
                setFieldValue(`tasks.${index}.assetId`, asset?.id ?? null);
              }}
              onInputChange={(_, input, reason) => {
                if (reason !== "input") return;
                if (assetTimer.current) clearTimeout(assetTimer.current);
                assetTimer.current = setTimeout(() => void searchAssets(input), 280);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset (type to search)"
                  error={Boolean(taskTouched?.assetId && taskErrors?.assetId)}
                  helperText={taskTouched?.assetId && taskErrors?.assetId}
                  sx={inputWhiteSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Device / request details"
              value={task.description}
              onChange={(e) => setFieldValue(`tasks.${index}.description`, e.target.value)}
              sx={inputWhiteSx}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function InlineEmployeeForm({
  addressInputId,
  onCreated,
  onDismiss,
}: {
  addressInputId: string;
  onCreated: (emp: Employee) => void;
  onDismiss: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [home, setHome] = useState({
    address: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
  });
  const [emp, setEmp] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  const patchHome = (partial: Partial<typeof home>) => {
    setHome((prev) => ({ ...prev, ...partial }));
  };

  const submit = async () => {
    setErr(null);
    setSaving(true);
    try {
      const empRes = await api.post<Employee>("/employees", {
        employee: {
          first_name: emp.first_name,
          last_name: emp.last_name,
          email: emp.email || undefined,
          status: "active",
          home_address: home.address || undefined,
          home_address2: home.address2 || undefined,
          home_city: home.city || undefined,
          home_state: home.state || undefined,
          home_zipcode: home.zipcode || undefined,
          home_country: home.country || undefined,
        },
      });
      onCreated(empRes.data);
    } catch {
      setErr("Failed to save. Check names and home address.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1, border: 1, borderColor: "divider" }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
        New employee
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Home address is stored on the employee. Search for an address to auto-fill, or enter fields manually.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 2,
        }}
      >
        <AddressAutocomplete
          inputId={addressInputId}
          label="Home address"
          value={home.address}
          onChange={(v) => patchHome({ address: v })}
          onAddressSelect={(fields) => {
            if (typeof fields === "string") return;
            patchHome({
              address: fields.address,
              address2: fields.address2,
              city: fields.city,
              state: fields.state,
              zipcode: fields.zipcode,
              country: fields.country,
            });
          }}
        />

        <FormControl variant="standard" fullWidth>
          <StandardInputLabel shrink htmlFor={`${addressInputId}-line2`}>
            Address line 2
          </StandardInputLabel>
          <InputBase
            id={`${addressInputId}-line2`}
            placeholder="Suite, unit, building"
            value={home.address2}
            onChange={(e) => patchHome({ address2: e.target.value })}
          />
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 3,
          mb: 2,
        }}
      >
        <FormControl variant="standard" fullWidth>
          <StandardInputLabel shrink htmlFor={`${addressInputId}-city`}>
            City
          </StandardInputLabel>
          <InputBase
            id={`${addressInputId}-city`}
            placeholder="City"
            value={home.city}
            onChange={(e) => patchHome({ city: e.target.value })}
          />
        </FormControl>
        <FormControl variant="standard" fullWidth>
          <StandardInputLabel shrink htmlFor={`${addressInputId}-state`}>
            State / province
          </StandardInputLabel>
          <InputBase
            id={`${addressInputId}-state`}
            placeholder="State"
            value={home.state}
            onChange={(e) => patchHome({ state: e.target.value })}
          />
        </FormControl>
        <FormControl variant="standard" fullWidth>
          <StandardInputLabel shrink htmlFor={`${addressInputId}-zip`}>
            Postal code
          </StandardInputLabel>
          <InputBase
            id={`${addressInputId}-zip`}
            placeholder="Postal code"
            value={home.zipcode}
            onChange={(e) => patchHome({ zipcode: e.target.value })}
          />
        </FormControl>
      </Box>

      <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
        <StandardInputLabel shrink htmlFor={`${addressInputId}-country`}>
          Country
        </StandardInputLabel>
        <InputBase
          id={`${addressInputId}-country`}
          placeholder="Country"
          value={home.country}
          onChange={(e) => patchHome({ country: e.target.value })}
        />
      </FormControl>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Employee
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="First name"
            value={emp.first_name}
            onChange={(e) => setEmp({ ...emp, first_name: e.target.value })}
            sx={inputWhiteSx}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Last name"
            value={emp.last_name}
            onChange={(e) => setEmp({ ...emp, last_name: e.target.value })}
            sx={inputWhiteSx}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            type="email"
            value={emp.email}
            onChange={(e) => setEmp({ ...emp, email: e.target.value })}
            sx={inputWhiteSx}
          />
        </Grid>
      </Grid>

      {err ? (
        <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
          {err}
        </Typography>
      ) : null}

      <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button variant="contained" size="small" disabled={saving} onClick={() => void submit()}>
          {saving ? <CircularProgress size={18} /> : "Save and use employee"}
        </Button>
        <Button variant="outlined" size="small" disabled={saving} onClick={onDismiss}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

export default function WorkOrderForm({ initialValues, onSubmit }: WorkOrderFormProps) {
  const initialTasks = useMemo(() => initialTasksFromWorkOrder(initialValues), [initialValues]);
  const isEdit = Boolean(initialValues.uuid);
  const { locations, isLoading: locLoading } = useLocations();

  return (
    <Box sx={{ mt: 3 }}>
      {locLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : null}
      <Formik
        enableReinitialize
        initialValues={{
          reason_for_return: initialValues.reason_for_return ?? initialValues.reasonForReturn ?? "",
          tasks: initialTasks,
        }}
        validationSchema={validationSchema}
        validate={(values) => {
          const errors: FormikErrors<{ tasks: WorkOrderTaskFormRow[]; reason_for_return: string }> &
            Record<string, unknown> = {};
          const active = values.tasks.filter((t) => !t._destroy);
          if (!active.length) {
            errors.general = "Add at least one request line";
          }
          const taskErrs: Array<FormikErrors<WorkOrderTaskFormRow> | undefined> = values.tasks.map(() => undefined);
          values.tasks.forEach((t, i) => {
            if (t._destroy) return;
            const row: FormikErrors<WorkOrderTaskFormRow> = {};
            if (!t.locationId && !t.employeeId) {
              row.employeeId = "Select an employee or a delivery location";
            }
            if (t.employeeId && !t.locationId) {
              if (!employeeHasShipTo(t.employee)) {
                row.employeeId =
                  "Employee needs an office location or home address, or pick a delivery location";
              }
            }
            if (ASSET_ACTIONS.has(t.action) && !t.assetId) {
              row.assetId = "Asset is required for this action";
            }
            if (Object.keys(row).length) taskErrs[i] = row;
          });
          if (taskErrs.some(Boolean)) {
            errors.tasks = taskErrs as FormikErrors<{ tasks: WorkOrderTaskFormRow[] }>["tasks"];
          }
          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          const attrs = buildTicketTasksAttributes(values.tasks);
          try {
            await onSubmit({
              reason_for_return: values.reason_for_return?.trim() || undefined,
              ticket_tasks_attributes: attrs,
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
          <Form>
            {(errors as { general?: string }).general ? (
              <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                {(errors as { general?: string }).general}
              </Typography>
            ) : null}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Overall notes (optional)"
                  name="reason_for_return"
                  value={values.reason_for_return}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={inputWhiteSx}
                />
              </Grid>
            </Grid>

            <FieldArray name="tasks">
              {({ remove, push }) => (
                <Box sx={{ mt: 2 }}>
                  {(() => {
                    let lineLabel = 0;
                    return values.tasks.map((task, index) => {
                      if (task._destroy) return null;
                      lineLabel += 1;
                      const visible = values.tasks.filter((t) => !t._destroy);
                      return (
                        <TaskLine
                          key={task.key}
                          task={task}
                          index={index}
                          isEdit={isEdit}
                          lineLabel={lineLabel}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          touched={touched}
                          canRemove={visible.length > 1}
                          locations={locations}
                          onRemove={() => {
                            if (task.id) {
                              setFieldValue(`tasks.${index}._destroy`, true);
                            } else {
                              remove(index);
                            }
                          }}
                        />
                      );
                    });
                  })()}

                  <Button startIcon={<AddIcon />} variant="outlined" onClick={() => push(emptyTask())} sx={{ mt: 1 }}>
                    Add request line
                  </Button>
                </Box>
              )}
            </FieldArray>

            <Box sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isEdit ? "Update" : "Submit"} work order
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
